<div class="bell-wrapper" (keydown.escape)="closeDropdown()">
  <div class="bell-trigger" [attr.aria-expanded]="dropdownOpen()">
    <kc-icon-button
      icon="bell"
      size="sm"
      variant="secondary"
      ariaLabel="Notifications"
      title="Notifications"
      (buttonClick)="toggleDropdown()"
    />
    @if (notificationsManager.unreadCount() > 0) {
      <span class="bell-trigger__badge">{{
        formatCount(notificationsManager.unreadCount())
      }}</span>
    }
  </div>

  @if (dropdownOpen()) {
    <div
      class="notifications-dropdown-overlay"
      aria-hidden="true"
      (click)="closeDropdown()"
    ></div>
    <div class="notifications-dropdown" role="menu">
      <div class="notifications-dropdown__header">
        <span>Notifications</span>
        <kc-button
          variant="link"
          size="s"
          text="Mark all read"
          [isDisabled]="!notificationsManager.canMarkAllRead()"
          (buttonClick)="markAllRead()"
        />
      </div>

      <div
        class="notifications-dropdown__filters"
        aria-label="Notification filters"
      >
        @for (filter of filters; track filter.value) {
          <button
            type="button"
            class="notifications-dropdown__filter"
            [class.notifications-dropdown__filter--active]="
              activeFilter() === filter.value
            "
            (click)="setFilter(filter.value)"
          >
            {{ filter.label }}
          </button>
        }
      </div>

      <div
        class="notifications-dropdown__body"
        (scroll)="onNotificationsScroll($event)"
      >
        @if (notificationsManager.error(); as error) {
          <kc-alert variant="warning" [text]="error" />
        } @else if (
          notificationsManager.loading() &&
          !notificationsManager.notifications().length
        ) {
          @for (row of skeletonRows; track row) {
            <kc-skeleton width="100%" height="48px" />
          }
        } @else if (!notificationsManager.notifications().length) {
          <p class="notifications-dropdown__empty">No notifications yet.</p>
        } @else if (!filteredNotifications().length) {
          <p class="notifications-dropdown__empty">
            {{
              activeFilter() === 'unread' &&
              notificationsManager.unreadCount() > 0 &&
              notificationsManager.hasMore()
                ? 'Older unread notifications are further down.'
                : activeFilter() === 'unread'
                  ? 'No unread notifications. Switch to All to see older ones.'
                  : 'No notifications for this filter.'
            }}
          </p>
        } @else {
          @for (
            notification of filteredNotifications();
            track notification.messageId
          ) {
            <article
              class="notification-row"
              [class.notification-row--unread]="notification.status !== 'read'"
              [class.notification-row--high-prominence]="
                notificationProminence(notification) === 'high'
              "
            >
              <div class="notification-row__dot" aria-hidden="true"></div>
              <div class="notification-row__body">
                <div class="notification-row__top">
                  <b>{{ notificationTitle(notification) }}</b>
                  <span
                    class="muted"
                    [attr.aria-label]="
                      'Created at ' + formatDateMs(notification.timestamp)
                    "
                    [title]="formatDateMs(notification.timestamp)"
                  >
                    {{ formatDateMs(notification.timestamp) }}
                  </span>
                </div>
                @if (notificationStatusLabel(notification); as statusLabel) {
                  <span
                    class="notification-row__status"
                    [class]="
                      'notification-row__status notification-row__status--' +
                      notificationStatusTone(notification)
                    "
                    [attr.aria-label]="'Notification status: ' + statusLabel"
                  >
                    {{ statusLabel }}
                  </span>
                }
                @if (notificationTradeSummary(notification); as trade) {
                  <div
                    class="notification-trade"
                    [attr.aria-label]="
                      (trade.side === 'sell' ? 'Sold ' : 'Bought ') +
                      trade.tokenAmountLabel +
                      ' for ' +
                      trade.kasAmountLabel
                    "
                  >
                    <div class="notification-trade__leg">
                      <app-token-logo
                        [logoUrl]="trade.from.logoUrl"
                        [label]="trade.from.ticker"
                        size="sm"
                      />
                      <div class="notification-trade__leg-text">
                        <span class="notification-trade__amount">{{
                          trade.from.amountLabel
                        }}</span>
                        <span class="notification-trade__ticker">{{
                          trade.from.ticker
                        }}</span>
                      </div>
                    </div>
                    <span class="notification-trade__arrow" aria-hidden="true"
                      >→</span
                    >
                    <div class="notification-trade__leg">
                      <app-token-logo
                        [logoUrl]="trade.to.logoUrl"
                        [label]="trade.to.ticker"
                        size="sm"
                      />
                      <div class="notification-trade__leg-text">
                        <span class="notification-trade__amount">{{
                          trade.to.amountLabel
                        }}</span>
                        <span class="notification-trade__ticker">{{
                          trade.to.ticker
                        }}</span>
                      </div>
                    </div>
                  </div>
                  @if (trade.unitPriceLabel) {
                    <p class="notification-trade__price muted">
                      @ {{ trade.unitPriceLabel }} each
                    </p>
                  }
                } @else {
                  <div class="notification-row__message">
                    @for (
                      row of notificationMessageRows(notification);
                      track $index
                    ) {
                      <p [innerHTML]="safeNotificationHtml(row)"></p>
                    }
                  </div>
                }
                <div class="notification-row__meta">
                  @if (notificationTransactionHash(notification); as txid) {
                    <span class="notification-row__tx">
                      <span class="muted">Tx ID:</span>
                      @if (
                        notificationExplorerHref(notification);
                        as explorerHref
                      ) {
                        <a
                          class="notification-row__txid-link"
                          [href]="explorerHref"
                          target="_blank"
                          rel="noopener noreferrer"
                          [attr.aria-label]="
                            'Open transaction ' + txid + ' in explorer'
                          "
                          [title]="txid"
                        >
                          <code>{{ shortIdentifier(txid) }}</code>
                        </a>
                      } @else {
                        <code [title]="txid">{{ shortIdentifier(txid) }}</code>
                      }
                      <kc-icon-button
                        variant="secondary"
                        size="xs"
                        icon="copy"
                        [ariaLabel]="
                          copiedTxid() === txid
                            ? 'Transaction id copied'
                            : 'Copy transaction id'
                        "
                        [title]="
                          copiedTxid() === txid
                            ? 'Copied'
                            : 'Copy transaction id'
                        "
                        (buttonClick)="copyTxid(txid)"
                      />
                    </span>
                  }
                </div>
                @if (notificationCta(notification); as cta) {
                  @if (cta.external) {
                    <a
                      class="notification-row__pill"
                      [href]="cta.href"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {{ cta.label }}
                    </a>
                  } @else {
                    <a
                      class="notification-row__pill"
                      [href]="cta.href"
                      (click)="closeDropdown()"
                    >
                      {{ cta.label }}
                    </a>
                  }
                }
              </div>
              @if (notification.status !== 'read') {
                <kc-icon-button
                  icon="check"
                  size="sm"
                  variant="secondary"
                  ariaLabel="Mark notification read"
                  (buttonClick)="markRead(notification)"
                />
              }
            </article>
          }
        }
      </div>

      <div class="notifications-dropdown__footer">
        @if (notificationsManager.hasMore()) {
          <kc-button
            variant="secondary"
            size="s"
            [text]="
              notificationsManager.loadingMore() ? 'Loading...' : 'Load more'
            "
            [isDisabled]="notificationsManager.loadingMore()"
            (buttonClick)="loadMore()"
          />
        }
        @if (notificationsManager.error()) {
          <kc-button
            variant="secondary"
            size="s"
            text="Retry"
            (buttonClick)="retry()"
          />
        }
        <kc-button
          variant="secondary"
          size="s"
          text="Delete all"
          [isDisabled]="!notificationsManager.notifications().length"
          (buttonClick)="clear()"
        />
        @if (
          notificationsManager.total() >
          notificationsManager.notifications().length
        ) {
          <span class="muted"
            >{{ formatCount(notificationsManager.total()) }} total</span
          >
        }
      </div>
    </div>
  }
</div>
